import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import PublishIcon from '@material-ui/icons/Publish';
import CategoryIcon from '@material-ui/icons/Category';
import SyncProblemIcon from '@material-ui/icons/SyncProblem';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { APPLICATION_PATHS } from '../../application-core/constants';
import AreYouSure from '../../core/AreYouSure';
import ProjectImportContainer from '../../core/project/ProjectImportContainer';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import SecondaryButton from '../../core/SecondaryButton';
import { globalStyles as styles } from '../../core/style/gstheme';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import GS from '../../grafsync/grafsync';
import SuggestionsManager from './SuggestionsManager';

const baseLink = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/reload';
const ADD_DEFAULT_CLASS = "addClasses";
const ADD_INSCHEME = "addScheme";
const MATERIALISE = "materialise";

class SchemeData extends Component {

  constructor(props) {
    super(props);
    this.state = {
      action: "",
      areYouSure: false,
      operation: "",
      sureMessage: []
    };
  }

  checkIfSure = (operation, sureMessage) => this.setState({ areYouSure: true, sureMessage, operation });

  onNotSure = () => this.setState({ areYouSure: false, sureMessage: [], operation: "" });

  onSure = async () => {
    this.setState({ areYouSure: false, sureMessage: [] });
    try {
      // We need to ensure messages are processed or batch lock will cause rejection
      await GS.waitForEditsToClear();
      GS.operate("batchupdate", this.props.graph, {
        action: this.state.operation,
        scheme: this.props.schemeIRI
      });
      this.setState({ operation: "" });
    }
    catch (e) {
      this.setState({ isErrored: { message: "Cannot perform operation." } });
    }
  };

  setAction = (action) => this.setState({ action });

  cancelAction = () => this.setState({ action: "" });

  render() {

    const { action, areYouSure, sureMessage } = this.state;
    const { graph, entitlements, schemeIRI, project, useSlashIRI, useUuidIRI, iriOptions, taxonomies, collections, useWorkflow,
      useXL, defaultClasses, classes, isReadOnly } = this.props;

    const includeImport = entitlements.includes(APPENTITLEMENTS.CAN_IMPORT);

    return (
      <ProjectConsumer>
        {({ defaultLang, skosXL, hasComponent, ontologyProjectAssociation }) => (
          <div style={{ padding: 30 }}>
            {action === "import" ?
              <div>
                <div style={{ float: 'right', marginBottom: 20 }}>
                  <SecondaryButton onClick={this.cancelAction}>Cancel Merge</SecondaryButton>
                </div>
                <Typography paragraph className={classes.mainHeading}>Merge Data</Typography>
                <Typography paragraph variant="body2">This will merge data onto this taxonomy as top concepts. We recommend running a quality check after import.</Typography>
                <ProjectImportContainer
                  baseLink={baseLink}
                  center={true}
                  spreadsheetOnly={true}
                  disableScreen={true}
                  includeOptions={false}
                  collections={collections}
                  taxonomies={taxonomies}
                  useSlashIRI={useSlashIRI}
                  useUuidIRI={useUuidIRI}
                  useOptions={{
                    baseIRI: schemeIRI,
                    lang: defaultLang,
                    useXL: useXL,
                    removeSpaces: iriOptions.removeSpaces,
                    replaceSpaces: iriOptions.replaceSpaces,
                    removeSpecialCharacters: iriOptions.removeSpecialCharacters,
                    replaceSpecialCharacters: iriOptions.replaceSpecialCharacters,
                    minimiseEncoding: iriOptions.minimiseEncoding,
                    prependText: iriOptions.xlOptions.prependText,
                    appendText: iriOptions.xlOptions.shouldAppendText ? defaultLang : ""
                  }}
                  subLink={"taxonomy=" + schemeIRI}
                  importContext={{
                    situation: "AttachToScheme",
                    resourceIRI: schemeIRI,
                    baseIRI: schemeIRI
                  }}
                  graph={graph}
                  id={project} />
              </div>
              :
              <div>
                <div style={{ textAlign: 'right', marginBottom: 36 }}>
                  {!isReadOnly && defaultClasses && defaultClasses.length > 0 &&
                    <span style={{ marginRight: 12 }}>
                      <Tooltip title="Add default classes to all concepts that are in the scheme">
                        <IconButton data-test="add-default-class" size="small" onClick={() => this.checkIfSure(ADD_DEFAULT_CLASS,
                          [
                            "This operation will add any default classes defined for the taxonomy to all concepts that have an 'In Scheme' value that is this taxonomy.",
                            "This is a batch operation and will lock the project."
                          ])}>
                          <CategoryIcon />
                        </IconButton>
                      </Tooltip>
                    </span>
                  }
                  {!isReadOnly && ontologyProjectAssociation && ontologyProjectAssociation.length > 0 &&
                    <span style={{ marginRight: 12 }}>
                      <Tooltip title="Synchronise all inverse and symmetric relationships">
                        <IconButton data-test="materialise" size="small" onClick={() => this.checkIfSure(MATERIALISE,
                          [
                            "This operation will add any missing inverse and symmetric relationships on concepts and SKOS-XL labels that are in the scheme.",
                            "Note that this will only apply to relationships that are wholly within the scheme or outbound from the concepts or labels in this scheme.",
                            "If there are other taxonomies with outbound symmetric or inverse relationships that target concepts or labels in this scheme, you will need to process them separately.",
                            "This is a batch operation and will lock the project."
                          ])}>
                          <SyncProblemIcon />
                        </IconButton>
                      </Tooltip>
                    </span>
                  }
                  {!isReadOnly &&
                    <span style={{ marginRight: 12 }}>
                      <Tooltip title="Make all concepts in the hierarchy belong to this scheme">
                        <IconButton data-test="add-inscheme" size="small" onClick={() => this.checkIfSure(ADD_INSCHEME,
                          [
                            "This operation will add all concepts that are in the hierarchy to the scheme (add 'In Scheme' for this taxonomy).",
                            "This is a batch operation and will lock the project."
                          ])}>
                          <PlaylistAddIcon />
                        </IconButton>
                      </Tooltip>
                    </span>
                  }
                  {includeImport &&
                    <Tooltip title="Merge a TSV, CSV or spreadsheet file onto this taxonomy">
                      <IconButton data-test="import-spreadsheet" size="small" onClick={() => this.setAction("import")}>
                        <PublishIcon />
                      </IconButton>
                    </Tooltip>
                  }
                </div>
                <SuggestionsManager
                  defaultType="NewConceptSuggestion"
                  schemeIRI={schemeIRI}
                  projectIRI={project}
                  components={hasComponent}
                  optionForXL={skosXL}
                  useWorkflow={useWorkflow}
                  useXL={useXL}
                  iriOptions={iriOptions}
                  defaultClasses={defaultClasses}
                  isVisible={true}
                  useSlashIRI={useSlashIRI}
                  useUuidIRI={useUuidIRI}
                  entitlements={entitlements}
                  defaultLang={defaultLang}
                  handleNotAvailable={true}
                  id={schemeIRI}
                  graph={graph} />
              </div>
            }
            {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message={sureMessage} />}
          </div>
        )}
      </ProjectConsumer>
    );
  }
}

SchemeData.propTypes = {
  classes: PropTypes.object,
  entitlements: PropTypes.array.isRequired,
  project: PropTypes.string.isRequired,
  defaultClasses: PropTypes.array,
  graph: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  collections: PropTypes.array,
  taxonomies: PropTypes.array,
  schemeIRI: PropTypes.string,
  useWorkflow: PropTypes.bool,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  useXL: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  iriOptions: PropTypes.object
};

export default withStyles(styles)(SchemeData);